import { useEffect } from "react";

/**
 * Per-page SEO metadata: title, description, canonical, Open Graph, Twitter,
 * and optional JSON-LD structured data.
 *
 * All values fall back to the site-wide defaults declared in public/index.html
 * when a page unmounts.
 *
 * Usage:
 *   usePageMeta({
 *     title: "CoachIQ Features | AI Tools for Soccer Coaches",
 *     description: "…",
 *     canonicalPath: "/features",
 *     ogImage: "https://uphauslabs.com/og/features.png",
 *     jsonLd: [{ "@context": "https://schema.org", ... }],
 *   });
 */
const CANONICAL_ORIGIN = "https://uphauslabs.com";

function ensureMeta(selector, createAttrs) {
  let el = document.querySelector(selector);
  if (el) return el;
  el = document.createElement("meta");
  Object.entries(createAttrs).forEach(([k, v]) => el.setAttribute(k, v));
  document.head.appendChild(el);
  return el;
}

function setAttrSaved(el, attr, value) {
  if (!el) return;
  el.setAttribute(`data-prev-${attr}`, el.getAttribute(attr) || "");
  el.setAttribute(attr, value);
}

function restoreAttr(el, attr) {
  if (!el) return;
  const prev = el.getAttribute(`data-prev-${attr}`);
  if (prev !== null) {
    if (prev) el.setAttribute(attr, prev);
    else el.removeAttribute(attr);
    el.removeAttribute(`data-prev-${attr}`);
  }
}

export default function usePageMeta({
  title,
  description,
  canonicalPath,
  ogTitle,
  ogDescription,
  ogType = "website",
  ogImage,
  twitterTitle,
  twitterDescription,
  twitterCard = "summary_large_image",
  jsonLd,
} = {}) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const canonicalUrl = canonicalPath ? `${CANONICAL_ORIGIN}${canonicalPath}` : null;

    // description
    const descEl = document.querySelector("meta[name='description']");
    if (description && descEl) setAttrSaved(descEl, "content", description);

    // canonical
    let canonicalEl = document.querySelector("link[rel='canonical']");
    if (canonicalUrl) {
      if (!canonicalEl) {
        canonicalEl = document.createElement("link");
        canonicalEl.setAttribute("rel", "canonical");
        document.head.appendChild(canonicalEl);
        canonicalEl.setAttribute("data-created", "1");
      }
      setAttrSaved(canonicalEl, "href", canonicalUrl);
    }

    // Open Graph
    const ogTitleEl = ensureMeta("meta[property='og:title']", { property: "og:title", content: "" });
    const ogDescEl = ensureMeta("meta[property='og:description']", { property: "og:description", content: "" });
    const ogUrlEl = ensureMeta("meta[property='og:url']", { property: "og:url", content: "" });
    const ogTypeEl = ensureMeta("meta[property='og:type']", { property: "og:type", content: "" });
    const ogImageEl = ogImage ? ensureMeta("meta[property='og:image']", { property: "og:image", content: "" }) : null;

    if (ogTitle || title) setAttrSaved(ogTitleEl, "content", ogTitle || title);
    if (ogDescription || description) setAttrSaved(ogDescEl, "content", ogDescription || description);
    if (canonicalUrl) setAttrSaved(ogUrlEl, "content", canonicalUrl);
    setAttrSaved(ogTypeEl, "content", ogType);
    if (ogImage) setAttrSaved(ogImageEl, "content", ogImage);

    // Twitter
    const twCardEl = ensureMeta("meta[name='twitter:card']", { name: "twitter:card", content: "" });
    const twTitleEl = ensureMeta("meta[name='twitter:title']", { name: "twitter:title", content: "" });
    const twDescEl = ensureMeta("meta[name='twitter:description']", { name: "twitter:description", content: "" });

    setAttrSaved(twCardEl, "content", twitterCard);
    if (twitterTitle || title) setAttrSaved(twTitleEl, "content", twitterTitle || title);
    if (twitterDescription || description) setAttrSaved(twDescEl, "content", twitterDescription || description);

    // JSON-LD structured data — attached with a data-attribute for cleanup
    const ldEls = [];
    if (jsonLd) {
      const arr = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      arr.forEach((obj, i) => {
        try {
          const script = document.createElement("script");
          script.type = "application/ld+json";
          script.setAttribute("data-page-meta", `1`);
          script.text = JSON.stringify(obj);
          document.head.appendChild(script);
          ldEls.push(script);
        } catch (e) { /* ignore malformed */ }
      });
    }

    return () => {
      document.title = prevTitle;
      restoreAttr(descEl, "content");
      if (canonicalEl) {
        if (canonicalEl.getAttribute("data-created") === "1") {
          canonicalEl.remove();
        } else {
          restoreAttr(canonicalEl, "href");
        }
      }
      restoreAttr(ogTitleEl, "content");
      restoreAttr(ogDescEl, "content");
      restoreAttr(ogUrlEl, "content");
      restoreAttr(ogTypeEl, "content");
      if (ogImageEl) restoreAttr(ogImageEl, "content");
      restoreAttr(twCardEl, "content");
      restoreAttr(twTitleEl, "content");
      restoreAttr(twDescEl, "content");
      ldEls.forEach((s) => s.remove());
    };
  }, [title, description, canonicalPath, ogTitle, ogDescription, ogType, ogImage, twitterTitle, twitterDescription, twitterCard, JSON.stringify(jsonLd || null)]);
}
